import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import { fmtDate, fmtTime, isPastFunction } from '../../utils/helpers';
import { openWhatsAppUrl } from '../../utils/whatsappInvite';

// "Share these functions" — the message a family sends to someone who is invited
// to SOME of the ceremonies: pick the functions, and each becomes a block of
// date, time, name and venue (with a map link when the function has one), under
// a warm line and over "From <the family>".
//
// Nothing is sent by a server: the text goes to the clipboard, to WhatsApp's
// own chooser (wa.me with no number), or to the phone's share sheet.

const DEFAULT_MESSAGE = 'With great joy we invite you to share these moments with us.';
const FROM_KEY = (eventId) => `se_share_from:${eventId}`;
const MSG_KEY = (eventId) => `se_share_msg:${eventId}`;

const read = (key, fallback) => {
  try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
};
const write = (key, value) => { try { localStorage.setItem(key, value); } catch { /* private mode */ } };

// A Maps link for whatever the function carries: "lat,lng" or a pasted URL.
const mapLink = (fn) => {
  const gps = String(fn.GpsLocation || '').trim();
  if (/^https?:\/\//i.test(gps)) return gps;
  if (/^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(gps)) return `https://maps.google.com/?q=${gps.replace(/\s/g, '')}`;
  const where = [fn.Venue, fn.VenueAddress, fn.City].filter(Boolean).join(', ');
  return where ? `https://maps.google.com/?q=${encodeURIComponent(where)}` : '';
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const dayName = (iso) => {
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '' : DAYS[d.getDay()];
};

export function useFunctionShare({ functions, event, onClose }) {
  const { user, organiser } = useAuth();
  const toast = useToast();

  // Only functions still to come can be invited to.
  const invitable = useMemo(() => functions.filter(f => !isPastFunction(f)), [functions]);
  const [picked, setPicked] = useState(() => invitable.map(f => String(f.FunctionId)));
  useEffect(() => { setPicked(invitable.map(f => String(f.FunctionId))); }, [invitable]);

  // "From …" is remembered per event; the first guess is the sender's own
  // surname ("Ailani Family"), else the event's side name, else the organiser.
  const guessFrom = `${user?.Surname || ''}`.trim()
    ? `${String(user.Surname).trim()} Family`
    : (event?.MaleSideName || organiser?.OrganiserName || '');
  const [from, setFrom] = useState(() => read(FROM_KEY(event?.EventId), guessFrom));
  const [message, setMessage] = useState(() => read(MSG_KEY(event?.EventId), DEFAULT_MESSAGE));
  useEffect(() => { write(FROM_KEY(event?.EventId), from); }, [from, event]);
  useEffect(() => { write(MSG_KEY(event?.EventId), message); }, [message, event]);

  const [withMap, setWithMap] = useState(true);

  const toggle = (id) => setPicked(list => (
    list.includes(String(id)) ? list.filter(x => x !== String(id)) : [...list, String(id)]
  ));
  const allOn = invitable.length > 0 && picked.length === invitable.length;
  const toggleAll = () => setPicked(allOn ? [] : invitable.map(f => String(f.FunctionId)));

  const chosen = useMemo(
    () => invitable.filter(f => picked.includes(String(f.FunctionId))),
    [invitable, picked]
  );

  const text = useMemo(() => {
    const lines = [];
    if (event?.EventName) lines.push(`*${event.EventName}*`, '');
    if (message.trim()) lines.push(message.trim(), '');
    chosen.forEach((fn, i) => {
      if (i) lines.push('');
      lines.push(`*${fn.FunctionName}*`);
      const day = dayName(fn.FunctionDate);
      lines.push(`📅 ${fmtDate(fn.FunctionDate)}${day ? ` (${day})` : ''}`);
      const start = fmtTime(fn.StartTime || fn.FunctionTime);
      const end = fmtTime(fn.EndTime);
      if (start) lines.push(`🕒 ${start}${end ? ` – ${end}` : ''}`);
      const where = [fn.Venue, fn.VenueAddress, fn.City].filter(Boolean).join(', ');
      if (where) lines.push(`📍 ${where}`);
      if (withMap) {
        const map = mapLink(fn);
        if (map) lines.push(map);
      }
    });
    // The family's name closes the invitation, bold as a whole line so it
    // stands out in the chat the way a signature should.
    if (from.trim()) lines.push('', `*From ${from.trim()}*`);
    return lines.join('\n');
  }, [event, message, chosen, from, withMap]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast('Message copied — paste it in any chat');
    } catch { toast('Could not copy — select the text and copy it'); }
  };

  // wa.me with no number: WhatsApp asks whom to send it to. The tab is reused
  // for every message, so sending to ten people leaves one WhatsApp tab open.
  const whatsapp = () => openWhatsAppUrl(`https://wa.me/?text=${encodeURIComponent(text)}`);

  const canShare = typeof navigator !== 'undefined' && !!navigator.share;
  const share = async () => {
    try { await navigator.share({ title: event?.EventName || 'Invitation', text }); } catch { /* dismissed */ }
  };

  // The preview shows it as WhatsApp will: *…* is bold, and the signature
  // line is marked so it can be highlighted.
  const preview = useMemo(() => text.split(String.fromCharCode(10)).map((line, i) => ({
    key: i,
    signature: line.startsWith('*From '),
    parts: line.split(/(\*[^*]+\*)/).filter(Boolean).map(p => (
      p.startsWith('*') && p.endsWith('*') && p.length > 2
        ? { text: p.slice(1, -1), bold: true }
        : { text: p, bold: false }
    )),
  })), [text]);

  return {
    preview,
    invitable, picked, toggle, allOn, toggleAll, chosen,
    from, setFrom, message, setMessage, withMap, setWithMap,
    text, copy, whatsapp, share, canShare, close: onClose, event,
  };
}
